﻿import { useCallback, useEffect, useState } from 'react';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import type { AdaptationSuggestion } from '../../application/dto/AdaptationSuggestion';
import type { DailyInspiration } from '../../application/dto/DailyInspiration';
import type { WorkoutSessionDetails } from '../../application/dto/WorkoutSessionDetails';
import type { DailyMissionsClient } from '../../application/services/DailyMissionsService';
import { UserChoice } from '../../domain/adaptive-guidance/enums';
import { RoutineCategory, TaskStatus } from '../../domain/routine/enums';
import { addLogicalDays } from '../../domain/shared/LogicalDate';
import type { DailyStepId, ExerciseLogId } from '../../domain/shared/ids';
import type { DailyStep } from '../../domain/routine/models';
import { useAscendSession } from '../design-system/session/useAscendSession';
import { TodayScreen } from '../design-system/screens/TodayScreen';
import { useDayModeAtmosphere } from '../context/useDayModeAtmosphere';
import { useTranslation } from '../design-system/data/translations';
import { AdaptiveGuidanceBanner, type AdaptiveGuidanceDecision } from '../components/AdaptiveGuidanceBanner';
import { Dialog } from '../components/Dialog';
import { DailyInspirationCard } from '../components/DailyInspirationCard';
import { ErrorState, LoadingState } from '../components/States';
import { WorkoutSessionPanel } from '../components/WorkoutSessionPanel';

interface TodayPageProps {
  readonly client: DailyMissionsClient;
}

const DATE_LOCALE_MAP: Record<string, string> = {
  'fr-FR': 'fr-FR',
  'en-US': 'en-US',
  'es-MX': 'es-MX',
};

function formatDate(logicalDate: string, locale: string): string {
  return new Intl.DateTimeFormat(DATE_LOCALE_MAP[locale] ?? 'fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${logicalDate}T00:00:00.000Z`));
}

function greeting(t: (key: string) => string, firstName: string): string {
  const hour = new Date().getHours();
  const base = hour < 12 ? t('hero.greeting.morning') : hour < 18 ? t('hero.greeting.afternoon') : t('hero.greeting.evening');
  const name = firstName.trim();
  return name ? `${base} ${name}` : base;
}

/**
 * Coordinateur de l'écran Aujourd'hui : câble le `DailyMissionsClient` existant sur
 * `useAscendSession` (mode, état émotionnel, présentation V7) et conserve l'orchestration
 * métier des missions (actions, réorganisation, séance de sport, inspiration, guidance
 * adaptative) — inchangée dans son fonctionnement, seulement redirigée vers `TodayScreen` pour
 * la composition visuelle.
 */
export function TodayPage({ client }: TodayPageProps) {
  const { t } = useTranslation();
  const session = useAscendSession(client);
  const { setDayMode } = useDayModeAtmosphere();

  const [busyStepId, setBusyStepId] = useState<DailyStepId>();
  const [feedback, setFeedback] = useState('');
  const [skipStep, setSkipStep] = useState<DailyStep>();
  const [postponeStep, setPostponeStep] = useState<DailyStep>();
  const [targetDate, setTargetDate] = useState('');
  const [reordering, setReordering] = useState(false);
  const [orderedIds, setOrderedIds] = useState<readonly DailyStepId[]>([]);
  const [saveOrderOpen, setSaveOrderOpen] = useState(false);
  const [reorderScope, setReorderScope] = useState<'today' | 'today_and_future'>('today');
  const [modeDialogOpen, setModeDialogOpen] = useState(false);
  const [emotionalStateDialogOpen, setEmotionalStateDialogOpen] = useState(false);
  const [inspiration, setInspiration] = useState<DailyInspiration>();
  const [inspirationLoading, setInspirationLoading] = useState(true);
  const [workout, setWorkout] = useState<WorkoutSessionDetails>();
  const [workoutBusy, setWorkoutBusy] = useState(false);
  const [suggestion, setSuggestion] = useState<AdaptationSuggestion>();
  const [suggestionBusy, setSuggestionBusy] = useState(false);
  const [guidanceDecision, setGuidanceDecision] = useState<AdaptiveGuidanceDecision>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const { routineDetails } = session;

  useEffect(() => {
    if (session.mode) setDayMode(session.mode);
  }, [session.mode, setDayMode]);

  useEffect(() => {
    let active = true;
    void client.getInspiration().then((result) => {
      if (!active) return;
      if (result.ok) setInspiration(result.value);
      setInspirationLoading(false);
    });
    return () => {
      active = false;
    };
  }, [client]);

  const sportStep = routineDetails?.steps.find((step) => step.category === RoutineCategory.sport);
  const sportActive = sportStep?.status === TaskStatus.inProgress;

  useEffect(() => {
    if (!sportActive || !routineDetails) return;
    let active = true;
    void client.getWorkout(routineDetails.routine.dayMode).then((result) => {
      if (active && result.ok) setWorkout(result.value);
    });
    return () => {
      active = false;
    };
  }, [client, sportActive, routineDetails]);

  useEffect(() => {
    if (!routineDetails || reordering) return;
    let active = true;
    void client.getAdaptationSuggestion(routineDetails.routine.logicalDate).then((result) => {
      if (active && result.ok) setSuggestion(result.value);
    });
    return () => {
      active = false;
    };
  }, [client, routineDetails, reordering]);

  const runAction = useCallback(
    async (
      step: DailyStep,
      action: (
        logicalDate: string,
        stepId: DailyStepId,
      ) => ReturnType<DailyMissionsClient['start']>,
      message: string,
    ) => {
      if (!routineDetails) return;
      setBusyStepId(step.id);
      setFeedback('');
      const result = await action(routineDetails.routine.logicalDate, step.id);
      if (result.ok) {
        session.applyRoutineDetails(result.value);
        setFeedback(message);
      }
      setBusyStepId(undefined);
    },
    [routineDetails, session],
  );

  const tomorrow = routineDetails ? addLogicalDays(routineDetails.routine.logicalDate, 1) : '';
  const inTwoDays = routineDetails ? addLogicalDays(routineDetails.routine.logicalDate, 2) : '';

  const cancelReordering = useCallback(() => {
    setReordering(false);
    setSaveOrderOpen(false);
    setOrderedIds([]);
    setFeedback(t('missions.reorg_annulee'));
  }, [t]);

  useEffect(() => {
    if (!reordering) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancelReordering();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [cancelReordering, reordering]);

  const moveMission = (stepId: DailyStepId, delta: -1 | 1) => {
    setOrderedIds((current) => {
      const from = current.indexOf(stepId);
      const to = from + delta;
      if (from < 0 || to < 0 || to >= current.length) return current;
      const moved = arrayMove([...current], from, to);
      const step = routineDetails?.steps.find((candidate) => candidate.id === stepId);
      setFeedback(`${step?.title ?? t('missions.label')} ${t('missions.deplacee')} ${to + 1}.`);
      return moved;
    });
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setOrderedIds((current) => {
      const from = current.indexOf(active.id as DailyStepId);
      const to = current.indexOf(over.id as DailyStepId);
      if (from < 0 || to < 0) return current;
      setFeedback(`${t('missions.deplacee')} ${to + 1}.`);
      return arrayMove([...current], from, to);
    });
  };

  const runWorkoutAction = async (
    action: () => ReturnType<DailyMissionsClient['reduceWorkout']>,
  ) => {
    setWorkoutBusy(true);
    const result = await action();
    if (result.ok) setWorkout(result.value);
    setWorkoutBusy(false);
  };
  const completeExercise = (exerciseId: ExerciseLogId, weight?: number) =>
    void runWorkoutAction(() =>
      client.completeExercise(
        routineDetails!.routine.logicalDate,
        exerciseId,
        weight !== undefined ? { userWeight: weight } : undefined,
      ),
    );
  const skipExercise = (exerciseId: ExerciseLogId) =>
    void runWorkoutAction(() => client.skipExercise(routineDetails!.routine.logicalDate, exerciseId));
  const replaceExercise = (exerciseId: ExerciseLogId) =>
    void runWorkoutAction(() => client.replaceExercise(routineDetails!.routine.logicalDate, exerciseId));
  const reduceWorkout = () =>
    void runWorkoutAction(() => client.reduceWorkout(routineDetails!.routine.logicalDate));

  const respondToSuggestion = async (choice: UserChoice) => {
    if (!routineDetails) return;
    setSuggestionBusy(true);
    const result = await client.respondToAdaptationSuggestion(
      routineDetails.routine.logicalDate,
      choice,
    );
    if (result.ok) {
      setSuggestion(result.value);
      setGuidanceDecision(choice === UserChoice.accepted ? 'accepted' : 'rejected');
      if (choice === UserChoice.accepted) {
        await session.reload();
        setFeedback(t('feedback.adaptation_applied'));
      } else {
        setFeedback(t('feedback.adaptation_kept'));
      }
    }
    setSuggestionBusy(false);
  };

  if (session.status === 'error' && !routineDetails) {
    return (
      <main className="page page--center">
        <ErrorState
          message={session.error?.message ?? t('error.loading')}
          onRetry={() => void session.reload()}
          {...(session.error?.code ? { code: session.error.code } : {})}
          {...(import.meta.env.DEV ? { diagnostic: client.getRuntimeDiagnostic() } : {})}
        />
      </main>
    );
  }
  if (!routineDetails) {
    return (
      <main className="page page--center">
        <LoadingState />
      </main>
    );
  }

  const confirmSkip = async () => {
    if (!skipStep) return;
    await runAction(skipStep, client.skip.bind(client), t('feedback.mission_skipped'));
    setSkipStep(undefined);
  };
  const confirmPostpone = async () => {
    if (!postponeStep || !targetDate) return;
    setBusyStepId(postponeStep.id);
    const result = await client.postpone(routineDetails.routine.logicalDate, postponeStep.id, targetDate);
    if (result.ok) {
      session.applyRoutineDetails(result.value);
      setFeedback(`${t('feedback.mission_postponed')} ${formatDate(targetDate, session.locale)}.`);
      setPostponeStep(undefined);
    }
    setBusyStepId(undefined);
  };

  // Les trois emplacements (Sport, Inspiration stoïcienne, Guidance adaptative) restent
  // structurellement toujours rendus — chacun affiche son contenu réel, un état de chargement,
  // ou un état vide factuel, mais ne disparaît jamais (voir revue visuelle V7 #6).
  const thematicContent = (
    <>
      <WorkoutSessionPanel
        details={sportActive ? workout : undefined}
        loading={sportActive && !workout}
        busy={workoutBusy}
        onComplete={completeExercise}
        onSkip={skipExercise}
        onReplace={replaceExercise}
        onReduce={reduceWorkout}
      />
      <DailyInspirationCard inspiration={inspiration} loading={inspirationLoading} />
      <AdaptiveGuidanceBanner
        suggestion={reordering ? undefined : suggestion}
        decision={guidanceDecision}
        busy={suggestionBusy}
        onAccept={() => void respondToSuggestion(UserChoice.accepted)}
        onReject={() => void respondToSuggestion(UserChoice.rejected)}
      />
    </>
  );

  const dialogs = (
    <>
      <Dialog
        open={saveOrderOpen}
        title={t('missions.enregistrer_ordre')}
        confirmLabel={t('missions.enregistrer_ordre')}
        onConfirm={() => {
          setBusyStepId(routineDetails.steps[0]?.id);
          void client
            .reorder(routineDetails.routine.logicalDate, orderedIds, reorderScope)
            .then((result) => {
              if (result.ok) {
                session.applyRoutineDetails(result.value);
                setReordering(false);
                setOrderedIds([]);
                setSaveOrderOpen(false);
                setFeedback(
                  reorderScope === 'today'
                    ? t('missions.ordre_enregistre')
                    : t('missions.ordre_enregistre_futur'),
                );
              }
              setBusyStepId(undefined);
            });
        }}
        onClose={() => setSaveOrderOpen(false)}
        busy={Boolean(busyStepId)}
      >
        <fieldset className="date-options">
          <legend>{t('missions.reorder_scope_legend')}</legend>
          <label>
            <input
              type="radio"
              name="reorder-scope"
              checked={reorderScope === 'today'}
              onChange={() => setReorderScope('today')}
            />
            {t('missions.reorder_scope_today')}
          </label>
          <label>
            <input
              type="radio"
              name="reorder-scope"
              checked={reorderScope === 'today_and_future'}
              onChange={() => setReorderScope('today_and_future')}
            />
            {t('missions.reorder_scope_future')}
          </label>
        </fieldset>
      </Dialog>
      <Dialog
        open={Boolean(skipStep)}
        title={t('missions.skip_title')}
        confirmLabel={t('missions.skip_confirm')}
        onConfirm={() => void confirmSkip()}
        onClose={() => setSkipStep(undefined)}
        busy={Boolean(busyStepId)}
        danger
      >
        <p>{t('missions.skip_body')}</p>
      </Dialog>
      <Dialog
        open={Boolean(postponeStep)}
        title={t('missions.postpone_title')}
        confirmLabel={t('missions.postpone_confirm')}
        onConfirm={() => void confirmPostpone()}
        onClose={() => setPostponeStep(undefined)}
        busy={Boolean(busyStepId)}
      >
        <fieldset className="date-options">
          <legend>{t('missions.postpone_legend')}</legend>
          <label>
            <input
              type="radio"
              name="target-date"
              value={tomorrow}
              checked={targetDate === tomorrow}
              onChange={() => setTargetDate(tomorrow)}
            />
            {t('missions.postpone_tomorrow')} <small>{formatDate(tomorrow, session.locale)}</small>
          </label>
          <label>
            <input
              type="radio"
              name="target-date"
              value={inTwoDays}
              checked={targetDate === inTwoDays}
              onChange={() => setTargetDate(inTwoDays)}
            />
            {t('missions.postpone_2days')} <small>{formatDate(inTwoDays, session.locale)}</small>
          </label>
          <label className="date-options__custom">
            {t('missions.postpone_custom')}
            <input
              aria-label={t('missions.postpone_custom_aria')}
              type="date"
              min={tomorrow}
              value={targetDate}
              onChange={(event) => setTargetDate(event.target.value)}
            />
          </label>
        </fieldset>
      </Dialog>
    </>
  );

  return (
    <TodayScreen
      session={session}
      greeting={greeting(t, session.firstName ?? '')}
      dateLabel={formatDate(routineDetails.routine.logicalDate, session.locale)}
      feedback={feedback}
      missions={{
        steps: routineDetails.steps,
        busyStepId,
        onStart: (step) => void runAction(step, client.start.bind(client), t('feedback.mission_started')),
        onComplete: (step) =>
          void runAction(step, client.complete.bind(client), t('feedback.mission_completed')),
        onReset: (step) =>
          void runAction(step, client.reset.bind(client), t('feedback.mission_reset')),
        onRequestSkip: setSkipStep,
        onRequestPostpone: (step) => {
          setPostponeStep(step);
          setTargetDate(tomorrow);
        },
      }}
      reorder={{
        active: reordering,
        orderedIds,
        sensors,
        onStart: () => {
          setOrderedIds(routineDetails.steps.map((step) => step.id));
          setReordering(true);
          setFeedback(t('missions.reorg_active'));
        },
        onCancel: cancelReordering,
        onSave: () => setSaveOrderOpen(true),
        onMoveMission: moveMission,
        onDragEnd,
      }}
      modeDialogOpen={modeDialogOpen}
      onOpenModeDialog={() => setModeDialogOpen(true)}
      onCloseModeDialog={() => setModeDialogOpen(false)}
      emotionalStateDialogOpen={emotionalStateDialogOpen}
      onOpenEmotionalStateDialog={() => setEmotionalStateDialogOpen(true)}
      onCloseEmotionalStateDialog={() => setEmotionalStateDialogOpen(false)}
      thematicContent={thematicContent}
      dialogs={dialogs}
    />
  );
}
